import type { TypedDocumentNode } from "@apollo/client";
import { useMutation } from "@apollo/client/react";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  CreateProductDocument,
  CreateProductMutationVariables,
} from "@repo/graphql/documents/product/mutation.generated";
import { X } from "lucide-react-native";
import React, { useCallback } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import FormInputDragFile from "../../../forms/dragFile";
import FormInputMoney from "../../../forms/money";
import FormInputText from "../../../forms/text";
import FormInputTextArea from "../../../forms/textArea";
import ModalProvider from "../../../globalProviders/modal";
import { showErrorToast } from "../../../globalProviders/toast";
import { Heading } from "../../../ui/heading";
import { Icon } from "../../../ui/icon";
import {
  ModalBody,
  ModalCloseButton,
  ModalFooter,
  ModalHeader,
} from "../../../ui/modal";
import { VStack } from "../../../ui/vstack";
import LoadingButton from "../../loadingButton";
import { productInputSchema } from "./schema";
import { CreateProductResult, ProductInputSchema } from "./types";

type Props = {
  /** Which brand's catalogue the product lands in. Passed explicitly rather than
   * read off the route, because the agent chat opens this modal from the Brief
   * card's product field, whose brand comes from the server (`brandId`), not the URL. */
  brandId: string;
  /** The brand's default currency, used to seed the price field. Callers must
   * wait for it to resolve before mounting this modal. */
  currency: string;
  /** The mutation to run: the logged-in `product.create`, or the guest chat's twin
   * (same variables and answer, see `useChatDocuments`). */
  mutation?: TypedDocumentNode<
    CreateProductResult,
    CreateProductMutationVariables
  >;
  mutationOptions: useMutation.Options<
    CreateProductResult,
    CreateProductMutationVariables
  >;
};

/**
 * The "Crea tu producto" modal body, shared by the campaign wizard and the agent
 * chat's product picker.
 *
 * It lived in `apps/center` and was reachable only from the manual campaign flow.
 * The agent chat's product field needs the very same form — same fields, same
 * validation, same mutation — so it moved here whole rather than being copied:
 * two drifting product forms is exactly the failure this avoids.
 */
const CreateProduct = React.memo((props: Props) => {
  const [createProduct, { loading }] = useMutation(
    props.mutation ?? CreateProductDocument,
    {
      ...props.mutationOptions,
      onCompleted: (productData) => {
        // A refused photo or a spent budget (the guest twin) answers with an error.
        const userErrors = productData.product.create.userErrors ?? [];
        if (userErrors.length > 0) {
          showErrorToast(userErrors[0].message);
          return;
        }
        ModalProvider.hide();
        props.mutationOptions.onCompleted?.(productData);
      },
    },
  );
  const formProps = useForm<ProductInputSchema>({
    resolver: yupResolver(productInputSchema),
    defaultValues: {
      name: "",
      price: { amount: "0", currency: props.currency },
      description: "",
      image: undefined,
    },
    mode: "onChange",
  });
  const onSubmit: SubmitHandler<ProductInputSchema> = useCallback(
    (data) => {
      createProduct({
        variables: {
          product: {
            brandId: props.brandId,
            name: data.name,
            description: data.description,
            link: "",
            price: data.price,
            image: data.image,
          },
        },
      });
    },
    [props.brandId, createProduct],
  );
  return (
    <>
      <ModalHeader>
        <Heading>Crea tu producto</Heading>
        <ModalCloseButton>
          <Icon as={X} size="lg" />
        </ModalCloseButton>
      </ModalHeader>
      <ModalBody>
        <VStack space="md" className="mt-3">
          <FormInputText
            control={formProps.control}
            name={"name"}
            placeholder={"Escribe el nombre de tu producto"}
            label={"Nombre del producto"}
            required={false}
            disabled={false}
            className={""}
            size={"sm"}
            keyboardType={"default"}
            textContentType={"none"}
            autoCapitalize={"none"}
            labelSize={"sm"}
          />
          <FormInputMoney
            control={formProps.control}
            name={"price"}
            placeholder={"Escribe el precio estimado"}
            label={"Precio estimado"}
            required={false}
            disabled={false}
            className={""}
            size={"sm"}
          />
          <FormInputTextArea
            control={formProps.control}
            name={"description"}
            placeholder={"Escribe la descripción de tu producto"}
            label={"Descripción"}
            required={false}
            disabled={false}
            className={""}
            size={"sm"}
            keyboardType={"default"}
            textContentType={"none"}
            autoCapitalize={"none"}
            labelSize={"sm"}
          />
          <FormInputDragFile
            control={formProps.control}
            name="image"
            labelSize="sm"
            iconSize="xl"
            helperText="Arrastra una foto de tu producto o búscalas acá"
            size="sm"
            multiple={false}
          />
        </VStack>
      </ModalBody>
      <ModalFooter>
        <LoadingButton
          action="brand"
          size="sm"
          rounded="lg"
          onPress={() => formProps.handleSubmit(onSubmit)()}
          loading={loading}
        >
          Crear producto
        </LoadingButton>
      </ModalFooter>
    </>
  );
});

CreateProduct.displayName = "CreateProduct";

export default CreateProduct;
